import { mat4 } from 'gl-matrix';

interface UndoRedoStacks {
    undoStack: mat4[];
    redoStack: mat4[];
}

/**
 * Service for managing undo/redo operations on registration transformations.
 * Maintains separate undo/redo stacks for each registration (keyed by SOP Instance UID).
 */
export class RegistrationUndoService {
    private stacksByRegistration = new Map<string, UndoRedoStacks>();
    private listeners = new Set<() => void>();

    private notifyListeners(): void {
        this.listeners.forEach((listener) => listener());
    }

    private getStacks(registrationId: string): UndoRedoStacks {
        if (!this.stacksByRegistration.has(registrationId)) {
            this.stacksByRegistration.set(registrationId, {
                undoStack: [],
                redoStack: [],
            });
        }
        return this.stacksByRegistration.get(registrationId)!;
    }

    /**
     * Subscribe to state changes for React hooks.
     */
    subscribe(listener: () => void): { unsubscribe: () => void } {
        this.listeners.add(listener);
        listener();
        return {
            unsubscribe: () => {
                this.listeners.delete(listener);
            },
        };
    }

    /**
     * Push the state before a change onto the undo stack.
     * Clears the redo stack since a new change invalidates redo history.
     * @param registrationId - The SOP Instance UID of the registration.
     * @param matrix - The matrix state to save.
     */
    pushState(registrationId: string, matrix: mat4): void {
        const stacks = this.getStacks(registrationId);
        stacks.undoStack.push(mat4.clone(matrix));
        stacks.redoStack = [];
        this.notifyListeners();
    }

    /**
     * Undo the last change and return the matrix to restore.
     * @param registrationId - The SOP Instance UID of the registration.
     * @param currentMatrix - The current matrix before undoing (pushed to redo stack).
     */
    undo(registrationId: string, currentMatrix: mat4): mat4 | undefined {
        const stacks = this.getStacks(registrationId);
        if (stacks.undoStack.length === 0) {
            return undefined;
        }

        stacks.redoStack.push(mat4.clone(currentMatrix));

        const previousState = stacks.undoStack.pop()!;
        this.notifyListeners();
        return previousState;
    }

    /**
     * Redo the last undone change and return the matrix to restore.
     * @param registrationId - The SOP Instance UID of the registration.
     * @param currentMatrix - The current matrix before redoing (pushed to undo stack).
     */
    redo(registrationId: string, currentMatrix: mat4): mat4 | undefined {
        const stacks = this.getStacks(registrationId);
        if (stacks.redoStack.length === 0) {
            return undefined;
        }

        stacks.undoStack.push(mat4.clone(currentMatrix));

        const nextState = stacks.redoStack.pop()!;
        this.notifyListeners();
        return nextState;
    }

    canUndo(registrationId: string): boolean {
        const stacks = this.stacksByRegistration.get(registrationId);
        return stacks ? stacks.undoStack.length > 0 : false;
    }

    canRedo(registrationId: string): boolean {
        const stacks = this.stacksByRegistration.get(registrationId);
        return stacks ? stacks.redoStack.length > 0 : false;
    }

    /**
     * Clear undo/redo stacks for a specific registration.
     * @param registrationId - The SOP Instance UID of the registration.
     */
    clear(registrationId: string): void {
        this.stacksByRegistration.delete(registrationId);
        this.notifyListeners();
    }

    /**
     * Clear all undo/redo stacks for all registrations.
     */
    clearAll(): void {
        this.stacksByRegistration.clear();
        this.notifyListeners();
    }
}
